import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import { HeaderFilterProvider, useHeaderFilters } from "../../context/HeaderFilterContext";
import { encryptQuery, decryptQuery } from "../../utils/encryption";
import useDynamicPageSize from "../../useDynamicPageSize";
import { toast, ToastContainer } from "react-toastify";
import Table from "../../core/common/dataTable";
import Footer from "../../Footer";
import nodeApi from "../../nodeApi";
import { Link, useNavigate, useLocation, useParams } from "react-router-dom";
import { useIsMobile } from "../../hooks/useIsMobile";

interface ChecklistType {
    id: number;
    title: string;
    is_active: number;
    created_at: string;
}

type ModalMode = "add" | "edit";

const InductionChecklist: React.FC = () => {
    const isMobile = useIsMobile(991);
    const pageSize = useDynamicPageSize();
    const { setFilters } = useHeaderFilters();
    const navigate = useNavigate();
    const location = useLocation();
    const { encryptedQuery } = useParams<{ encryptedQuery?: string }>();

    const [data, setData] = useState<ChecklistType[]>([]);
    const [totalItems, setTotalItems] = useState<number>(0);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [loading, setLoading] = useState(true);
    const [tableHeight, setTableHeight] = useState<number>(0);
    const isInitialFetch = useRef(true);

    const [modalMode, setModalMode] = useState<ModalMode>("add");
    const [formData, setFormData] = useState({
        title: "",
        is_active: 0,
    });
    const [formError, setFormError] = useState<{
        title?: string;
    }>({});
    const [editId, setEditId] = useState<number | null>(null);
    const [deleteId, setDeleteId] = useState<number | null>(null);
    const isSubmittingRef = useRef(false);

    /* ================= HEIGHT ================= */
    useLayoutEffect(() => {
        const calculateHeight = () => {
            const baseOffset = 194;
            const extraOffset = window.innerWidth <= 991 ? -43 : -81;
            const fixedOffset = baseOffset + extraOffset;
            setTableHeight(window.innerHeight - fixedOffset);
        };

        calculateHeight();
        window.addEventListener("resize", calculateHeight);
        return () => window.removeEventListener("resize", calculateHeight);
    }, []);

    /* ================= URL DECRYPT ================= */
    useEffect(() => {
        if (encryptedQuery) {
            try {
                const decoded = decryptQuery(decodeURIComponent(encryptedQuery));
                if (decoded?.currentPage) {
                    setCurrentPage(Number(decoded.currentPage));
                }
            } catch { }
        }
    }, [encryptedQuery]);

    /* ================= FETCH LIST ================= */
    const fetchChecklist = async () => {
        try {
            setLoading(true);

            const res = await nodeApi<any>("/induction-checklist/induction-list", {
                method: "POST",
                body: {
                    currentPage,
                    pageSize,
                },
            });

            setData(res?.data || []);
            setTotalItems(res?.meta?.total || 0);
        } catch {
            toast.error("Failed to fetch checklist");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!pageSize) return;

        if (isInitialFetch.current) {
            isInitialFetch.current = false;
            return;
        }
        fetchChecklist();
    }, [currentPage, pageSize]);

    /* ================= HEADER FILTER ================= */
    useEffect(() => {
        const Filters = () => (
            <div className="px-2 px-md-2">
                <div className="payheader-filters d-flex align-items-center flex-wrap ">
                    <div className="d-flex align-items-center gap-2">
                        <button
                            className="btn btn-outline-primary add_btn_size"
                            onClick={() => openModal("add")}
                        >
                            <i className="ti ti-circle-plus" />
                        </button>
                    </div>
                </div>
            </div>
        );

        setFilters(<Filters />);
        return () => setFilters(null);
    }, [currentPage]);

    /* ================= PAGINATION ================= */
    const handlePageChange = (page: number) => {
        setCurrentPage(page);

        const encrypted = encryptQuery({
            currentPage: page,
            pageSize,
            module: "inductionChecklist",
            ts: Date.now(),
        });

        navigate(`/induction-checklist/${encodeURIComponent(encrypted)}`);
    };

    /* ================= MODAL ================= */
    const openModal = (mode: ModalMode, record?: ChecklistType) => {
        setModalMode(mode);
        setFormError({}); // clear errors

        if (mode === "add") {
            setFormData({ title: "", is_active: 0 });
            setEditId(null);
        }

        if (mode === "edit" && record) {
            setFormData({
                title: record.title,
                is_active: record.is_active,
            });
            setEditId(record.id);
        }

        const modal = document.getElementById("checklist_modal");
        if (modal) {
            (window as any).bootstrap.Modal.getOrCreateInstance(modal).show();
        }
    };

    const hideModal = () => {
        const modal = document.getElementById("checklist_modal");
        if (!modal) return;

        const instance = (window as any).bootstrap.Modal.getInstance(modal);
        instance?.hide();

        // Reset AFTER modal animation completes
        modal.addEventListener(
            "hidden.bs.modal",
            () => {
                setFormData({ title: "", is_active: 0 });
                setEditId(null);
                setFormError({});
            },
            { once: true }
        );
    };

    /* ================= SAVE ================= */
    const handleSubmit = async () => {
        if (isSubmittingRef.current) return;

        const errors: any = {};

        if (!formData.title.trim()) {
            errors.title = "Title is required";
        }

        if (Object.keys(errors).length > 0) {
            setFormError(errors);
            return;
        }

        isSubmittingRef.current = true;

        try {
            await nodeApi(
                modalMode === "add"
                    ? "/induction-checklist"
                    : `/induction-checklist/${editId}`,
                {
                    method: modalMode === "add" ? "POST" : "PUT",
                    body: formData,
                }
            );

            toast.success(
                modalMode === "add"
                    ? "Checklist added successfully"
                    : "Checklist updated successfully"
            );

            hideModal();
            fetchChecklist();
        } catch {
            toast.error("Something went wrong");
        } finally {
            isSubmittingRef.current = false;
        }
    };

    /* ================= DELETE ================= */
    const handleDelete = async () => {
        if (!deleteId) return;

        try {
            await nodeApi(`/induction-checklist/${deleteId}`, {
                method: "DELETE",
            });

            toast.success("Checklist deleted successfully");
            fetchChecklist();
        } catch {
            toast.error("Failed to delete checklist");
        }
    };

    /* ================= TABLE ================= */
    const columns = [
        {
            title: "Title",
            render: (_: any, r: ChecklistType) => {
                const titleText = r.title || "-";

                return (
                    <div style={{
                        display: 'grid',      // Grid handles the 'min-width: 0' rule better than block
                        width: '100%',
                        overflow: 'hidden'
                    }}>
                        <span
                            title={titleText}
                            style={{
                                display: "block",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                minWidth: 0
                            }}
                        >
                            {titleText}
                        </span>
                    </div>
                );
            },
        },
        {
            title: "Status",
            render: (_: any, r: ChecklistType) => (
                <span
                    className={`badge ${r.is_active ? "bg-success" : "bg-danger"
                        }`}
                >
                    {r.is_active ? "Active" : "Inactive"}
                </span>
            ),
        },
        {
            title: "Actions",
            render: (_: any, r: ChecklistType) => (
                <div className="action-icon d-inline-flex gap-2">
                    <Link to="#" onClick={() => openModal("edit", r)} title="Edit Checklist">
                        <i className="ti ti-edit" />
                    </Link>
                    <Link
                        to="#"
                        onClick={() => {
                            setDeleteId(r.id);
                            const modal = document.getElementById("top-modal");
                            if (modal) {
                                (window as any).bootstrap.Modal.getOrCreateInstance(modal).show();
                            }
                        }}
                        title="Delete Checklist"
                    >
                        <i
                            className="ti ti-trash"
                        />
                    </Link>
                </div>
            ),
        },
    ];

    return (
        <>
            <HeaderFilterProvider>
                <div className="page-wrapper d-flex flex-column">
                    <div className="content flex-grow-1">
                        <div className="card card-margin-bottom">
                            <div
                                className="card-body p-0"
                                style={{ height: tableHeight, display: "flex", flexDirection: "column" }}
                            >
                                <Table
                                    style={{ flex: 1 }}
                                    dataSource={data}
                                    columns={columns}
                                    currentPage={currentPage}
                                    pageSize={pageSize}
                                    totalItems={totalItems}
                                    onPageChange={handlePageChange}
                                    loading={loading}
                                />
                            </div>
                        </div>
                    </div>

                    {/* ADD / EDIT MODAL */}
                    <div className="modal fade" id="checklist_modal">
                        <div className="modal-dialog modal-md modal-dialog-centered">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h4 className="modal-title">
                                        {modalMode === "add" ? "Add Checklist" : "Edit Checklist"}
                                    </h4>
                                    <button className="btn-close" onClick={hideModal} />
                                </div>

                                <div className="modal-body">
                                    <div className="mb-3">
                                        <label>
                                            Title <span className="text-danger">*</span>
                                        </label>

                                        <input
                                            className={`form-control ${formError.title ? "is-invalid" : ""}`}
                                            value={formData.title}
                                            onChange={(e) => {
                                                setFormData((p) => ({
                                                    ...p,
                                                    title: e.target.value,
                                                }));

                                                if (formError.title) {
                                                    setFormError((p) => ({ ...p, title: "" }));
                                                }
                                            }}
                                            onBlur={() => {
                                                if (!formData.title.trim()) {
                                                    setFormError((p) => ({
                                                        ...p,
                                                        title: "Title is required",
                                                    }));
                                                }
                                            }}
                                        />

                                        {formError.title && (
                                            <div className="invalid-feedback d-block">
                                                {formError.title}
                                            </div>
                                        )}
                                    </div>

                                    <div className="form-check">
                                        <input
                                            type="checkbox"
                                            className="form-check-input"
                                            id="is_active"
                                            checked={!!formData.is_active}
                                            onChange={(e) =>
                                                setFormData((p) => ({
                                                    ...p,
                                                    is_active: e.target.checked ? 1 : 0,
                                                }))
                                            }
                                        />
                                        <label className="form-check-label" htmlFor="is_active">
                                            Active
                                        </label>
                                    </div>
                                </div>

                                <div className="modal-footer d-flex justify-content-end gap-2">
                                    <button className="btn btn-light" onClick={hideModal}>
                                        Cancel
                                    </button>
                                    <button className="btn btn-primary" onClick={handleSubmit}>
                                        {modalMode === "add" ? "Add" : "Update"}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* DELETE MODAL */}
                    <div id="top-modal" className="modal fade">
                        <div className="modal-dialog modal-top">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h4 className="modal-title">Delete Checklist</h4>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        data-bs-dismiss="modal"
                                    />
                                </div>

                                <div className="modal-body">
                                    <p>Are you sure you want to delete this checklist?</p>
                                </div>

                                <div className="modal-footer d-flex justify-content-end gap-2">
                                    <button className="btn btn-light" data-bs-dismiss="modal">
                                        Cancel
                                    </button>

                                    <button
                                        className="btn btn-primary"
                                        data-bs-dismiss="modal"
                                        onClick={() => deleteId && handleDelete()}
                                    >
                                        Delete
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <Footer />
                </div>
            </HeaderFilterProvider>

            <ToastContainer position="top-right" autoClose={3000} />
        </>
    );
};

export default InductionChecklist;